/* eslint-disable max-len */
import React, { PureComponent } from 'react';
import Video from 'video.js';
import type { Props } from '../../video.types';
import { convertToFrames } from '../../video.utils';
import { WHITE_LABEL_TYPES } from '../../video.constant';

class DefaultPlayer extends PureComponent<Props> {
    // eslint-disable-next-line react/sort-comp
    videoNode: any;

    player: any;

    componentDidMount = () => {
        const { videoDidMount } = this.props;

        videoDidMount(this);
        this.createPlayer();
    };

    createPlayer = () => {
        const {
            video: { durationInSeconds },
            subtitle,
            subtitles,
            videoDidReady,
            videoDidError,
            options: { timeStamp, lang, showSubtitles, autoplay },
            setVideoRenditions,
            renditionsArray,
            hasHls,
            forceRendition,
            apiRejected,
            videoDidPlay,
            videoDidPause,
            videoDidEnd,
            renditionDidChange,
        } = this.props;

        const forcedRenditions = renditionsArray.filter(
            rendition => rendition.h === parseInt(forceRendition, 10)
        );
        const sortedRenditionsArray = renditionsArray.sort(
            (x, y) => (hasHls && !x.h ? -9999 : y.h - x.h)
        );

        const finalRenditionsArray = forcedRenditions.length
            ? forcedRenditions
            : sortedRenditionsArray;

        this.player = Video(this.videoNode, this.getVideoJSOptions(), () => {
            // Set up time stamp
            if (timeStamp > 0 && timeStamp <= Math.floor(durationInSeconds)) {
                this.seek(timeStamp, 'secs');
            }

            if (this.player.muted()) {
                this.setVolume(0);
            }

            this.player.fill(true);

            if (autoplay) {
                this.player.addClass('vjs-forceHide'); // hide poster when reload
            }

            renditionDidChange(
                hasHls && !forceRendition ? 'auto' : finalRenditionsArray[0].h.toString()
            );

            this.player.on('play', () => videoDidPlay());
            this.player.on('pause', () => videoDidPause());
            this.player.on('ended', () => videoDidEnd());
            this.player.on('error', () => {
                const currSources = this.player.currentSources();
                const currSource = this.player.currentSource();
                const newSources = currSources.filter(source => source.src !== currSource.src);

                if (!newSources || !newSources.length) {
                    apiRejected('SOURCE_LOADING_REJECTED');

                    return;
                }

                renditionDidChange(hasHls && !forceRendition ? 'auto' : newSources[0].h);
                setVideoRenditions([...new Set(newSources.map(source => source.h))]);

                this.player.src(newSources);
                videoDidError(this.player.error());
            });

            // Set up subtitles
            let defaultSubtitles = '';

            if (Array.isArray(subtitles) && subtitles.length) {
                defaultSubtitles = subtitles.find(sub => Boolean(sub.default));
                defaultSubtitles = defaultSubtitles ? defaultSubtitles.label : subtitles[0].label;
            }

            if (lang) {
                subtitles.forEach(subtitle => {
                    if (subtitle.lang === lang) {
                        defaultSubtitles = subtitle.label;
                    }
                });
            }

            const initialSubtitle: string = showSubtitles ? defaultSubtitles : subtitle;

            this.addTextTracks(subtitles, initialSubtitle);

            videoDidReady();
        });
    };

    getVideoJSOptions = () => {
        const {
            video: { posterFrame, whiteLabel },
            options: { autoplay, muted, clearLoaders, loop },
            renditionsArray,
            forceRendition,
            hasHls,
            userAgent: { isSafari }
        } = this.props;

        const forcedRenditions = renditionsArray.filter(
            rendition => rendition.h === parseInt(forceRendition, 10)
        );

        const sortedRenditionsArray = renditionsArray.sort(
            (x, y) => (hasHls && !x.h ? -9999 : y.h - x.h)
        );

        const finalRenditionsArray = forcedRenditions.length
            ? forcedRenditions
            : sortedRenditionsArray;

        const finalMp4RenditionsArray = finalRenditionsArray.filter(item => item.ext === 'mp4');

        const isWhitelabeled =
            typeof whiteLabel === 'boolean'
                ? whiteLabel
                : whiteLabel !== WHITE_LABEL_TYPES.ENDFRAME;

        return {
            html5: {
                nativeTextTracks: false,
                nativeAudioTracks: false,
                nativeVideoTracks: false,
                hls: {
                    limitRenditionByPlayerDimensions: false,
                    overrideNative: true,
                },
            },
            sources: isSafari ? finalMp4RenditionsArray: finalRenditionsArray,
            poster: autoplay ? '' : (posterFrame.poster || posterFrame.large),
            loadingSpinner: !clearLoaders,
            autoplay,
            muted: autoplay || muted,
            loop: loop && isWhitelabeled,
        };
    };

    // Methods
    play = () => this.player.play();

    pause = () => this.player.pause();

    seek = (to: number, format: string = 'fps') => {
        const {
            video: { fps, duration },
            seek,
            addAdditionalFramesToSkip,
        } = this.props;

        const playerTime = format !== 'fps' ? to : to / fps;
        const frame = format !== 'fps' ? convertToFrames(to, fps) : to;
        const bufferedTime = this.player.bufferedPercent() * this.player.duration();

        if (bufferedTime < playerTime) {
            this.player.addClass('vjs-seeking-no-buffer');
            const timeWhenSeeking = this.player.currentTime();

            const timeUpdateListener = () => {
                if (timeWhenSeeking !== this.player.currentTime()) {
                    this.player.removeClass('vjs-seeking-no-buffer');
                    this.player.off('timeupdate', timeUpdateListener);
                }
            };

            this.player.on('timeupdate', timeUpdateListener);
        }

        // On IOS 13 when we skip it goes back approximately 0.35 seconds than the actual time
        // This is a know issue in IOS 13 and has been causing issue for FA
        // Hence adding this number to the player time to match up
        // Refs:
        // https://jsfiddle.net/bhL6am3j/1/
        // https://github.com/videojs/video.js/issues/6316
        const magicNumber = 0.45;

        // check to make sure adding the magicNumber doesn't exceed the duration of the video
        const seekTime = addAdditionalFramesToSkip && playerTime + magicNumber < duration ? playerTime + magicNumber : playerTime;

        this.player.currentTime(seekTime);
        seek(frame, this.player.currentTime());
    };

    reload = () => {
        this.player.addClass('vjs-forceHide'); // hide poster when reload

        const { frame, videoDidPause } = this.props;

        this.player.load();
        this.player.one('loadedmetadata', () => {
            this.seek(frame, 'fps');
            videoDidPause();
        });
    };

    dispose = () => this.player.dispose();

    // Getters
    getCurrentTime = () => this.player.currentTime();

    muted = (flag: ?boolean) => this.player.muted(flag);

    getDuration = () => this.player.duration();

    ended = () => this.player.ended();

    remainingTimeDisplay = () => this.player.remainingTimeDisplay();

    bufferedPercent = () => this.player.bufferedPercent();

    // Setters
    src = (source: string) => this.player.src(source);

    setVideoSource = (quality: number) => {
        const currentTime = this.player.currentTime();

        const { subtitles, subtitle, renditionsArray, hasHls, renditionDidChange } = this.props;

        const autoQualityObject = renditionsArray.find(rendition => !rendition.h);
        const selectedRendition = renditionsArray.filter(rendition => rendition.h === parseInt(quality, 10));

        this.player.src(quality === 'auto' && hasHls ? autoQualityObject : selectedRendition);
        this.player.load();
        this.player.currentTime(currentTime);
        this.player.on('canplay', () => this.player.play());
        document.removeEventListener('canplay', () => this.player.play());
        this.addTextTracks(subtitles, subtitle);

        renditionDidChange(quality);
    };

    setTextTrack = (selected: string) => {
        const { textTrackDidChange } = this.props;

        const tracks = this.player.textTracks();

        for (let i = 0; i < tracks.length; i += 1) {
            const track = tracks[i];

            if (track.label === selected) {
                track.mode = 'showing';
                textTrackDidChange(selected);
            } else {
                track.mode = 'disabled';
            }
        }
    };

    addTextTracks = (subtitles: Array<Object>, initialSubtitle: ?string = '') => {
        subtitles.forEach(subtitle =>
            this.player.addRemoteTextTrack(
                {
                    ...subtitle,
                    default: initialSubtitle ? false : subtitle.default, // initialSubtitle take higher priority than default from studio
                    kind: 'descriptions',
                },
                false
            )
        );

        if (initialSubtitle) {
            this.setTextTrack(initialSubtitle);
        }
    };

    setVolume = (fraction: number) => {
        if (fraction <= 0 && this.player.volume() === 0) return;
        if (fraction >= 1 && this.player.volume() === 1) return;

        const { volumeDidChange } = this.props;

        if (fraction < 0) {
            this.player.volume(0);
            volumeDidChange(0);

            return;
        }

        if (fraction >= 1) {
            this.player.volume(1);
            volumeDidChange(1);

            return;
        }

        if (fraction > 0 && this.player.muted()) {
            this.player.muted(false);
        }

        this.player.volume(fraction);
        volumeDidChange(fraction);
    };

    render = () => {
        const {
            showControls,
            video: { posterFrame, vidName },
            // options: { autoplay },
            isOverlayOpen,
            userAgent: { isMobile },
        } = this.props;

        // const poster = (autoplay || !isAndroid) ? '' : (posterFrame.poster || posterFrame.large);

        return (
            <div
                className={`default-player-wrapper ${
                    showControls ? 'controls-visible' : 'controls-hidden'
                } ${isOverlayOpen ? 'overlay-open' : 'overlay-hidden'} ${isMobile ? 'is-mobile' : ''}`}
                aria-label={vidName}
            >
                <div data-vjs-player>
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <video
                        // poster={poster}
                        className="video-js"
                        playsInline
                        webkit-playsinline="true"
                        ref={node => {
                            this.videoNode = node;
                        }}
                    />
                </div>
            </div>
        );
    };
}

export default DefaultPlayer;
